import type {ReactNode} from 'react';
import type {TFunction} from 'i18next';

export interface SelectOption {
    /** Repeated values under different headings share one selection. */
    value: string;
    label: string;
    /** Grey text at the row's right end, e.g. an email or "27 people". Searched, never on chips. */
    detail?: string;
    /** Outer heading, above group. */
    section?: string;
    group?: string;
    /** An 18px flag or icon, shared by the row and chip. */
    adornment?: ReactNode;
    /** MultiSelect chip tone. */
    tone?: 'success' | 'error';
    disabled?: boolean;
}

export interface ResolvedOption extends SelectOption {
    unavailable?: boolean;
}

// Without an explicit loading flag, an empty-value "Any …" option is not evidence of a loaded list.
export const isSettled = (options: SelectOption[], loading: boolean | undefined): boolean =>
    loading === false || (loading === undefined && options.some(option => !!option.value));

export const isGrouped = (options: SelectOption[]): boolean =>
    options.some(option => !!option.group || !!option.section);

/** Identical group names in different sections remain separate. */
export const headingKey = (option: SelectOption): string => `${option.section ?? ''}\u001f${option.group ?? ''}`;

/** Repeated values still need a unique key for each row. */
export const optionKey = (option: SelectOption): string => `${headingKey(option)}\u001f${option.value}`;

/** MUI requires contiguous groups; preserve first appearance within each heading level. */
export const gatherGroups = <T extends SelectOption>(options: T[]): T[] => {
    if (!isGrouped(options)) return options;

    const sections = new Map<string, Map<string, T[]>>();
    for (const option of options) {
        const section = option.section ?? '';
        const group = option.group ?? '';
        let groups = sections.get(section);
        if (!groups) {
            groups = new Map<string, T[]>();
            sections.set(section, groups);
        }

        const members = groups.get(group);
        if (members) members.push(option);
        else groups.set(group, [option]);
    }

    return Array.from(sections.values()).flatMap(groups => Array.from(groups.values()).flat());
};

export const filterBySearch = <T extends SelectOption>(options: T[], inputValue: string): T[] => {
    const needle = inputValue.trim().toLowerCase();
    if (!needle) return options;

    return options.filter(option => [option.label, option.detail, option.group, option.section]
        .some(text => (text || '').toLowerCase().includes(needle)));
};

/** Keep the stored name while loading; mark it unavailable only after the list has loaded. */
export const unknownOption = (
    value: string,
    name: string | undefined,
    settled: boolean,
    t: TFunction
): ResolvedOption => {
    const label = name || value;

    return settled
        ? {value, label: t('{{name}} (no longer available)', {name: label}), group: t('No longer available'), unavailable: true}
        : {value, label};
};
